import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  getTranslatedDescription,
  SEOLanguage,
} from "@/data/structuredDataTranslations";
import { trackContactAction } from "@/utils/contactTracking";

const AccommodationsFAQ: React.FC = () => {
  const { language } = useLanguage();
  const lang = language as SEOLanguage;

  const faqs = [
    {
      question: getTranslatedDescription("faqCheckInOut", lang),
      answer: getTranslatedDescription("faqCheckInOutAnswer", lang),
    },
    {
      question: getTranslatedDescription("faqBreakfast", lang),
      answer: getTranslatedDescription("faqBreakfastAnswer", lang),
    },
    {
      question: getTranslatedDescription("faqViews", lang),
      answer: getTranslatedDescription("faqViewsAnswer", lang),
    },
    {
      question: getTranslatedDescription("faqAmenities", lang),
      answer: getTranslatedDescription("faqAmenitiesAnswer", lang),
    },
  ];

  const sectionTitle = {
    en: "Your Questions Answered",
    de: "Ihre Fragen Beantwortet",
    fr: "Vos Questions Répondues",
    "zh-CN": "您的问题已解答",
    ko: "여러분의 질문에 답변드립니다",
  };

  const sectionSubtitle = {
    en: "Everything you need to know about our accommodations",
    de: "Alles, was Sie über unsere Unterkünfte wissen müssen",
    fr: "Tout ce que vous devez savoir sur nos hébergements",
    "zh-CN": "关于我们住宿的一切信息",
    ko: "숙박 시설에 대해 알아야 할 모든 것",
  };

  return (
    <section
      className="py-20 bg-gradient-to-b from-gray-50 to-white"
      data-oid="x_9fzra"
    >
      <div className="max-w-4xl mx-auto px-4" data-oid="d7_jiza">
        {/* Section Header */}
        <div className="text-center mb-12" data-oid="vr6tx:8">
          <h2
            className="text-3xl md:text-4xl font-playfair text-gray-900 mb-4"
            data-oid="j288oc_"
          >
            {sectionTitle[lang]}
          </h2>
          <p
            className="text-lg text-gray-600 max-w-2xl mx-auto"
            data-oid="_vfohgt"
          >
            {sectionSubtitle[lang]}
          </p>
        </div>

        {/* FAQ Accordion */}
        <Accordion
          type="single"
          collapsible
          className="w-full space-y-4"
          data-oid="zqhl.1_"
        >
          {faqs.map((faq, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
              className="border border-gray-200 rounded-lg px-6 bg-white shadow-sm hover:shadow-md transition-shadow duration-300"
              data-oid="g3ocg_-"
            >
              <AccordionTrigger
                className="text-left text-lg font-medium text-gray-900 hover:text-alpine-gold transition-colors py-6"
                data-oid="p_la48w"
              >
                {faq.question}
              </AccordionTrigger>
              <AccordionContent
                className="text-gray-600 leading-relaxed pb-6"
                data-oid="3.ctbba"
              >
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Contact CTA */}
        <div className="mt-12 text-center" data-oid="-m8emgs">
          <p className="text-gray-600 mb-4" data-oid="zqcdkw:">
            {lang === "de" && "Haben Sie weitere Fragen?"}
            {lang === "fr" && "Avez-vous d'autres questions?"}
            {lang === "zh-CN" && "还有其他问题吗？"}
            {lang === "ko" && "추가 질문이 있으신가요?"}
            {lang === "en" && "Have more questions?"}
          </p>
          <a
            href={`mailto:info@beatenberg-resortspa.ch?subject=${
              lang === "de"
                ? "Anfrage zu Unterkünften"
                : lang === "fr"
                  ? "Demande concernant les hébergements"
                  : lang === "zh-CN"
                    ? "住宿咨询"
                    : lang === "ko"
                      ? "숙박 문의"
                      : "Inquiry about Accommodations"
            }`}
            onClick={() => trackContactAction('concierge', 'email', {
              email_address: 'info@beatenberg-resortspa.ch',
              contact_source: 'accommodations_faq_direct',
              page_location: 'accommodations_faq_section'
            })}
            className="inline-flex items-center text-alpine-gold hover:text-pine-green font-medium transition-colors contact-action-track"
            data-oid="6jc_334"
          >
            {lang === "de" && "Kontaktieren Sie uns"}
            {lang === "fr" && "Contactez-nous"}
            {lang === "zh-CN" && "联系我们"}
            {lang === "ko" && "문의하기"}
            {lang === "en" && "Contact Us"}
            <svg
              className="ml-2 w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              data-oid="okxt2bi"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
                data-oid="z-e95p:"
              />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default AccommodationsFAQ;
